import { FormEvent, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  AdminApi,
  AdminUserItem,
  AdminUserUpdatePayload,
} from '../../services/adminApi'
import { formatDbDateTimeVi } from '../../utils/dbDateDisplay'
import { useAuthStore } from '../../store/authStore'

const ROLE_OPTIONS = ['STUDENT', 'ADMIN'] as const
const STATUS_OPTIONS = ['', 'ACTIVE', 'INACTIVE', 'BANNED'] as const

const statusChipClass = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'bg-green-100 text-green-800'
    case 'INACTIVE':
      return 'bg-yellow-100 text-yellow-800'
    case 'BANNED':
      return 'bg-red-100 text-red-700'
    default:
      return 'bg-m2-surface-high text-m2-on-surface'
  }
}

const statusLabel = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'Hoạt động'
    case 'INACTIVE':
      return 'Tạm dừng'
    case 'BANNED':
      return 'Đã khóa'
    default:
      return status
  }
}

const emptyForm = (): AdminUserUpdatePayload & { roles: string[] } => ({
  email: '',
  fullName: '',
  firstName: '',
  phone: '',
  cccd: '',
  address: '',
  roles: ['STUDENT'],
  status: 'ACTIVE',
})

const AdminUsersPage = () => {
  const currentUserId = useAuthStore((s) => s.user?.id)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [items, setItems] = useState<AdminUserItem[]>([])
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  const [q, setQ] = useState('')
  const [role, setRole] = useState('')
  const [status, setStatus] = useState('')

  const [editing, setEditing] = useState<AdminUserItem | null>(null)
  const [form, setForm] = useState(emptyForm())

  const loadData = async (opts?: { page?: number }) => {
    const nextPage = opts?.page ?? page
    setLoading(true)
    try {
      const res = await AdminApi.listUsers({
        page: nextPage,
        limit: 20,
        q: q.trim() || undefined,
        role: role || undefined,
        status: status || undefined,
      })
      setItems(res.data.items)
      setTotalPages(res.data.pagination.totalPages)
      setTotal(res.data.pagination.total)
    } catch {
      toast.error('Không tải được danh sách user')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  const applyFilters = () => {
    if (page === 1) {
      void loadData({ page: 1 })
    } else {
      setPage(1)
    }
  }

  const openEdit = (user: AdminUserItem) => {
    setEditing(user)
    setForm({
      email: user.email,
      fullName: user.fullName,
      firstName: user.firstName,
      phone: user.phone || '',
      cccd: user.cccd || '',
      address: user.address || '',
      roles: user.roles?.length ? [...user.roles] : ['STUDENT'],
      status: user.status,
    })
  }

  const closeEdit = () => {
    setEditing(null)
    setForm(emptyForm())
  }

  const toggleRole = (roleName: string) => {
    setForm((prev) => {
      const has = prev.roles.includes(roleName)
      const next = has ? prev.roles.filter((r) => r !== roleName) : [...prev.roles, roleName]
      return { ...prev, roles: next.length ? next : ['STUDENT'] }
    })
  }

  const saveEdit = async (e: FormEvent) => {
    e.preventDefault()
    if (!editing) return
    setActionLoading(true)
    try {
      const payload: AdminUserUpdatePayload = {
        email: form.email?.trim(),
        fullName: form.fullName?.trim(),
        firstName: form.firstName?.trim(),
        phone: form.phone?.trim() || '',
        cccd: form.cccd?.trim() || null,
        address: form.address?.trim() || null,
        roles: form.roles,
        status: form.status,
      }
      const res = await AdminApi.updateUser(editing.id, payload)
      toast.success(res.message || 'Đã cập nhật user')
      closeEdit()
      await loadData()
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Cập nhật thất bại'
      toast.error(msg)
    } finally {
      setActionLoading(false)
    }
  }

  const isAdminTarget = (user: AdminUserItem) =>
    user.roles?.includes('ADMIN') || user.roles?.includes('SUPER_ADMIN')

  const lock = async (user: AdminUserItem) => {
    if (user.id === currentUserId) {
      toast.error('Không thể khóa tài khoản của chính mình')
      return
    }
    if (isAdminTarget(user)) {
      toast.error('Không thể khóa tài khoản admin')
      return
    }
    const reason = window.prompt('Lý do khóa tài khoản?', 'ADMIN_MANUAL')
    if (reason === null) return
    setActionLoading(true)
    try {
      await AdminApi.lockUser(user.id, reason.trim() || 'ADMIN_MANUAL')
      toast.success('Đã khóa tài khoản')
      await loadData()
      if (editing?.id === user.id) {
        const refreshed = await AdminApi.getUser(user.id)
        openEdit(refreshed.data)
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Khóa thất bại'
      toast.error(msg)
    } finally {
      setActionLoading(false)
    }
  }

  const unlock = async (user: AdminUserItem) => {
    setActionLoading(true)
    try {
      await AdminApi.unlockUser(user.id)
      toast.success('Đã mở khóa tài khoản')
      await loadData()
      if (editing?.id === user.id) {
        const refreshed = await AdminApi.getUser(user.id)
        openEdit(refreshed.data)
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Mở khóa thất bại'
      toast.error(msg)
    } finally {
      setActionLoading(false)
    }
  }

  const addWatchlist = async (user: AdminUserItem) => {
    const reason = window.prompt('Lý do đưa vào watchlist?', 'MANUAL')
    if (reason === null) return
    setActionLoading(true)
    try {
      await AdminApi.addToWatchlist(user.id, { reason: reason.trim() || 'MANUAL' })
      toast.success('Đã thêm vào watchlist')
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Thêm watchlist thất bại'
      toast.error(msg)
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-m2-on-surface">Quản lý user</h1>
        <p className="mt-1 text-sm text-m2-on-surface-variant">
          Tìm kiếm, cập nhật thông tin và khóa / mở khóa tài khoản ({total} user)
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-m2-outline-variant bg-m2-surface-container p-3">
        <label className="flex min-w-[200px] flex-1 flex-col gap-1 text-xs text-m2-on-surface-variant">
          Tìm kiếm
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyFilters()
            }}
            placeholder="Email, tên, SĐT..."
            className="rounded border border-m2-outline-variant bg-white px-3 py-2 text-sm text-m2-on-surface"
          />
        </label>
        <label className="flex w-36 flex-col gap-1 text-xs text-m2-on-surface-variant">
          Vai trò
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="rounded border border-m2-outline-variant bg-white px-3 py-2 text-sm text-m2-on-surface"
          >
            <option value="">Tất cả</option>
            {ROLE_OPTIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <label className="flex w-40 flex-col gap-1 text-xs text-m2-on-surface-variant">
          Trạng thái
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded border border-m2-outline-variant bg-white px-3 py-2 text-sm text-m2-on-surface"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s || 'all'} value={s}>
                {s ? statusLabel(s) : 'Tất cả'}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={applyFilters}
          className="rounded bg-m2-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90"
        >
          Lọc
        </button>
      </div>

      <div className="overflow-hidden rounded-lg border border-m2-outline-variant bg-m2-surface-container">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-m2-surface-low text-left text-xs uppercase text-m2-on-surface-variant">
              <tr>
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">SĐT</th>
                <th className="px-3 py-2">Roles</th>
                <th className="px-3 py-2">Trạng thái</th>
                <th className="px-3 py-2">Ngày tạo</th>
                <th className="px-3 py-2">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-center text-m2-on-surface-variant">
                    Đang tải...
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-center text-m2-on-surface-variant">
                    Không có user
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((row) => (
                  <tr key={row.id} className="border-t border-m2-outline-variant">
                    <td className="px-3 py-2">
                      <div className="font-medium text-m2-on-surface">{row.fullName}</div>
                      <div className="text-xs text-m2-on-surface-variant">{row.email}</div>
                    </td>
                    <td className="px-3 py-2 text-m2-on-surface">{row.phone || '-'}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(row.roles || []).map((r) => (
                          <span
                            key={r}
                            className="rounded bg-m2-primary/10 px-1.5 py-0.5 text-[11px] text-m2-primary"
                          >
                            {r}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`rounded px-2 py-1 text-xs ${statusChipClass(row.status)}`}>
                        {statusLabel(row.status)}
                      </span>
                      {row.lockReason && (
                        <div className="mt-1 text-[11px] text-m2-on-surface-variant">
                          {row.lockReason}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-m2-on-surface-variant">
                      {row.createdAt ? formatDbDateTimeVi(row.createdAt) : '-'}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="text-xs font-medium text-m2-primary hover:underline"
                          onClick={() => openEdit(row)}
                          disabled={actionLoading}
                        >
                          Sửa
                        </button>
                        {row.status === 'BANNED' ? (
                          <button
                            type="button"
                            className="text-xs font-medium text-green-700 hover:underline"
                            onClick={() => void unlock(row)}
                            disabled={actionLoading}
                          >
                            Mở khóa
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="text-xs font-medium text-red-600 hover:underline disabled:opacity-40"
                            onClick={() => void lock(row)}
                            disabled={
                              actionLoading ||
                              row.id === currentUserId ||
                              isAdminTarget(row)
                            }
                          >
                            Khóa
                          </button>
                        )}
                        <button
                          type="button"
                          className="text-xs font-medium text-amber-700 hover:underline disabled:opacity-40"
                          onClick={() => void addWatchlist(row)}
                          disabled={actionLoading}
                        >
                          Watchlist
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-m2-outline-variant px-3 py-2 text-sm">
            <span className="text-m2-on-surface-variant">
              Trang {page} / {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                className="rounded border border-m2-outline-variant px-3 py-1 disabled:opacity-40"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Trước
              </button>
              <button
                type="button"
                className="rounded border border-m2-outline-variant px-3 py-1 disabled:opacity-40"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Sau
              </button>
            </div>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Đóng"
            onClick={closeEdit}
          />
          <div className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-m2-outline-variant bg-eh-cream p-5 shadow-eh-soft">
            <h2 className="text-lg font-semibold text-m2-on-surface">Sửa user #{editing.id}</h2>
            <form className="mt-4 space-y-3" onSubmit={saveEdit}>
              <label className="block text-xs text-m2-on-surface-variant">
                Email
                <input
                  required
                  type="email"
                  value={form.email || ''}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="mt-1 w-full rounded border border-m2-outline-variant bg-white px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-xs text-m2-on-surface-variant">
                Họ tên
                <input
                  required
                  type="text"
                  value={form.fullName || ''}
                  onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                  className="mt-1 w-full rounded border border-m2-outline-variant bg-white px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-xs text-m2-on-surface-variant">
                Tên gọi
                <input
                  required
                  type="text"
                  value={form.firstName || ''}
                  onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                  className="mt-1 w-full rounded border border-m2-outline-variant bg-white px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-xs text-m2-on-surface-variant">
                Số điện thoại
                <input
                  type="text"
                  value={form.phone || ''}
                  onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  className="mt-1 w-full rounded border border-m2-outline-variant bg-white px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-xs text-m2-on-surface-variant">
                CCCD
                <input
                  type="text"
                  value={form.cccd || ''}
                  onChange={(e) => setForm((f) => ({ ...f, cccd: e.target.value }))}
                  className="mt-1 w-full rounded border border-m2-outline-variant bg-white px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-xs text-m2-on-surface-variant">
                Địa chỉ
                <input
                  type="text"
                  value={form.address || ''}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  className="mt-1 w-full rounded border border-m2-outline-variant bg-white px-3 py-2 text-sm"
                  placeholder="Địa chỉ xuất hóa đơn"
                />
              </label>
              <fieldset>
                <legend className="text-xs text-m2-on-surface-variant">Vai trò</legend>
                <div className="mt-1 flex flex-wrap gap-3">
                  {ROLE_OPTIONS.map((r) => (
                    <label key={r} className="flex items-center gap-1.5 text-sm text-m2-on-surface">
                      <input
                        type="checkbox"
                        checked={form.roles.includes(r)}
                        onChange={() => toggleRole(r)}
                        disabled={editing.id === currentUserId && r === 'ADMIN'}
                      />
                      {r}
                    </label>
                  ))}
                </div>
              </fieldset>
              <label className="block text-xs text-m2-on-surface-variant">
                Trạng thái
                <select
                  value={form.status || 'ACTIVE'}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  disabled={editing.id === currentUserId}
                  className="mt-1 w-full rounded border border-m2-outline-variant bg-white px-3 py-2 text-sm"
                >
                  <option value="ACTIVE">Hoạt động</option>
                  <option value="INACTIVE">Tạm dừng</option>
                  <option value="BANNED">Đã khóa</option>
                </select>
              </label>

              <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
                <div className="flex gap-2">
                  {editing.status === 'BANNED' ? (
                    <button
                      type="button"
                      disabled={actionLoading}
                      onClick={() => void unlock(editing)}
                      className="rounded border border-green-600 px-3 py-2 text-sm text-green-700 hover:bg-green-50"
                    >
                      Mở khóa
                    </button>
                  ) : (
                    <button
                      type="button"
                      disabled={
                        actionLoading ||
                        editing.id === currentUserId ||
                        isAdminTarget(editing)
                      }
                      onClick={() => void lock(editing)}
                      className="rounded border border-red-500 px-3 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-40"
                    >
                      Khóa
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={actionLoading}
                    onClick={() => void addWatchlist(editing)}
                    className="rounded border border-amber-500 px-3 py-2 text-sm text-amber-800 hover:bg-amber-50 disabled:opacity-40"
                  >
                    Watchlist
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={closeEdit}
                    className="rounded border border-m2-outline-variant px-3 py-2 text-sm"
                  >
                    Hủy
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading}
                    className="rounded bg-m2-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                  >
                    {actionLoading ? 'Đang lưu...' : 'Lưu'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminUsersPage
